import { useMemo, useState } from "react";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import type { GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import { collectAllPayLinksForExport } from "@/domain/payLinks/collectAllPayLinksForExport";
import { downloadPayLinksCsv, payLinksCsvFileName } from "@/domain/payLinks/exportPayLinksCsv";
import { exportPayLinksCsvFromServer } from "@/features/invoices/api/exportPayLinksCsv";
import {
  isInvoiceAmountFilterValid,
  validateInvoiceFilterValues,
  serializeInvoiceFiltersToSearchParams,
} from "@/domain/invoices/invoiceFilterState";
import {
  filterPayLinks,
  payLinksWithUrl,
} from "@/domain/payLinks/filterPayLinks";
import { DownloadListWorkspace } from "@/features/invoices/components/DownloadListWorkspace";
import { InvoiceCursorPager } from "@/features/invoices/components/InvoiceCursorPager";
import { InvoiceFiltersPanel } from "@/features/invoices/components/InvoiceFiltersPanel";
import { InvoiceTableToolbar } from "@/features/invoices/components/InvoiceTableToolbar";
import { PayLinksTable } from "@/features/invoices/components/PayLinksTable";
import { StatChipInline } from "@/features/invoices/components/StatChipInline";
import { useCustomers } from "@/features/invoices/hooks/useCustomers";
import { useInvoiceFilters } from "@/features/invoices/hooks/useInvoiceFilters";
import { usePayLinkSearch } from "@/features/invoices/hooks/usePayLinkSearch";
import { useRowSelection } from "@/features/invoices/hooks/useRowSelection";
import { PageHeader } from "@/features/layout/components/PageHeader";
import type { PayLinkRow } from "@/domain/payLinks/payLinkRow";
import type { InvoiceSearchRequest } from "@/types/api";
import { isoDateToUnixEnd, isoDateToUnixStart } from "@/utils/format/isoDate";
import { useFilterSearchButton } from "@/lib/query/useFilterSearchButton";
import { cn } from "@/utils/cn";

const EMPTY_DEFAULT_STATUSES: string[] = [];

function hasPayLink(row: PayLinkRow): boolean {
  return Boolean(row.hosted_url?.trim());
}

function majorToMinorUnits(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

function buildPayLinkSearchRequest(applied: {
  customerIds: string[];
  statuses: string[];
  dateFrom: string;
  dateTo: string;
  dueDateFrom: string;
  dueDateTo: string;
  amountMin: string;
  amountMax: string;
}): InvoiceSearchRequest {
  return {
    page: 0,
    page_size: 100,
    customer_ids:
      applied.customerIds.length > 0 ? applied.customerIds : undefined,
    statuses: applied.statuses.length > 0 ? applied.statuses : undefined,
    created_from: isoDateToUnixStart(applied.dateFrom),
    created_to: isoDateToUnixEnd(applied.dateTo),
    due_from: isoDateToUnixStart(applied.dueDateFrom),
    due_to: isoDateToUnixEnd(applied.dueDateTo),
    ...(isInvoiceAmountFilterValid(applied.amountMin, applied.amountMax)
      ? {
          amount_due_min: majorToMinorUnits(applied.amountMin),
          amount_due_max: majorToMinorUnits(applied.amountMax),
        }
      : {}),
  };
}

export function PayLinksWorkspace() {
  const { withGate } = usePlanAccess();
  const [billingGate, setBillingGate] = useState<{
    reason: BillingGateReason;
    capability: GatedCapability;
  } | null>(null);

  const openGate = (reason: BillingGateReason, capability: GatedCapability) => {
    setBillingGate({ reason, capability });
  };

  const filters = useInvoiceFilters(EMPTY_DEFAULT_STATUSES);
  const applied = filters.applied;

  const customersQuery = useCustomers();
  const customers = customersQuery.data?.items ?? [];

  const { data, isLoading, isFetching, isPlaceholderData, error } =
    usePayLinkSearch({
    customerIds: applied.customerIds,
    statuses: applied.statuses,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    dueDateFrom: applied.dueDateFrom,
    dueDateTo: applied.dueDateTo,
    amountMin: applied.amountMin,
    amountMax: applied.amountMax,
    pageSize: filters.pageSize,
    after: filters.after,
  });

  const rawItems = data?.items ?? [];
  const { searchLoading, markSearchStarted } = useFilterSearchButton(
    filters.appliedSearchParams.toString(),
    isLoading,
    isFetching,
    isPlaceholderData,
  );
  const filteredByStripe = rawItems;

  const items = useMemo(
    () => filterPayLinks(filteredByStripe, { tableSearch: filters.tableSearch }),
    [filteredByStripe, filters.tableSearch],
  );

  const selection = useRowSelection(items, (row) => row.invoice_id, hasPayLink);

  const pageSelectableCount = useMemo(
    () => items.filter(hasPayLink).length,
    [items],
  );

  const clearSelection = () => selection.clear();

  const handleToggleAllOnPage = () => {
    selection.toggleAllOnPage();
  };

  const handleToggleRow = (id: string) => {
    selection.toggle(id);
  };

  const withLinkCount = useMemo(
    () => payLinksWithUrl(filteredByStripe).length,
    [filteredByStripe],
  );

  const selectedWithLink = useMemo(
    () =>
      items.filter((r) => hasPayLink(r) && selection.selected.has(r.invoice_id)),
    [items, selection.selected],
  );

  const [csvToast, setCsvToast] = useState<string | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);

  const handleSearch = () => {
    markSearchStarted(
      serializeInvoiceFiltersToSearchParams(
        filters.draft,
        new URLSearchParams(),
      ).toString(),
    );
    filters.commitSearch(filters.draft);
    selection.clear();
    setCsvToast(null);
  };

  const runCsvExport = (rows: PayLinkRow[]) => {
    const exportRows = payLinksWithUrl(rows);
    if (exportRows.length === 0) return;
    const filename = payLinksCsvFileName();
    const reason = withGate("pay_links_csv", () => {
      downloadPayLinksCsv(exportRows, filename);
      setCsvToast(
        `Downloaded ${filename} · ${exportRows.length} link${exportRows.length === 1 ? "" : "s"}`,
      );
    });
    if (reason) openGate(reason, "pay_links_csv");
  };

  const runCsvExportAllMatching = async () => {
    const reason = withGate("pay_links_csv", () => undefined);
    if (reason) {
      openGate(reason, "pay_links_csv");
      return;
    }
    setCsvLoading(true);
    setCsvToast(null);
    try {
      if (!filters.tableSearch.trim()) {
        const { rowCount, filename } = await exportPayLinksCsvFromServer(
          buildPayLinkSearchRequest(applied),
        );
        if (rowCount === 0) return;
        setCsvToast(
          `Downloaded ${filename} · ${rowCount} link${rowCount === 1 ? "" : "s"}`,
        );
        return;
      }
      const all = await collectAllPayLinksForExport({
        customerIds: applied.customerIds,
        statuses: applied.statuses,
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        dueDateFrom: applied.dueDateFrom,
        dueDateTo: applied.dueDateTo,
        amountMin: applied.amountMin,
        amountMax: applied.amountMax,
      });
      const filtered = filterPayLinks(all, { tableSearch: filters.tableSearch });
      runCsvExport(filtered);
    } finally {
      setCsvLoading(false);
    }
  };

  const appliedFilterInvalid =
    validateInvoiceFilterValues(applied).length > 0;
  const csvDisabled =
    filters.isDirty || appliedFilterInvalid || selectedWithLink.length === 0;
  const allMatchingWithLink = payLinksWithUrl(filteredByStripe);

  const csvExportActions = (
    <div className="flex flex-wrap items-center gap-2">
      {filters.isDirty ? (
        <span className="text-xs font-semibold text-[var(--warn)]">
          Search to apply filters
        </span>
      ) : null}
      <button
        type="button"
        className="btn-main btn-export-zip shrink-0 text-sm"
        disabled={csvDisabled}
        onClick={() => runCsvExport(selectedWithLink)}
      >
        Copy {selectedWithLink.length} selected link
        {selectedWithLink.length === 1 ? "" : "s"} (CSV)
      </button>
      {selectedWithLink.length > 0 &&
      selectedWithLink.length < allMatchingWithLink.length ? (
        <button
          type="button"
          className="link-quiet text-sm font-semibold"
          disabled={filters.isDirty || csvLoading}
          onClick={() => void runCsvExportAllMatching()}
        >
          {csvLoading ? "Collecting all pages…" : "Copy all matching instead"}
        </button>
      ) : selectedWithLink.length === 0 && !filters.isDirty ? (
        <button
          type="button"
          className="btn-soft shrink-0 text-sm"
          disabled={allMatchingWithLink.length === 0 || csvLoading}
          onClick={() => void runCsvExportAllMatching()}
        >
          {csvLoading ? "Collecting all pages…" : "Copy all matching links (CSV)"}
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      <DownloadListWorkspace
        pinned={
          <>
            <PageHeader
              title="Download Pay Links"
              breadcrumbSuffix="Download Pay Links"
              className="page-head-row--compact"
            />

            <InvoiceFiltersPanel
              draft={filters.draft}
              applied={applied}
              isDirty={filters.isDirty}
              customers={customers}
              customersLoading={customersQuery.isLoading}
              customersError={null}
              onDraftChange={filters.setDraft}
              onSearch={handleSearch}
              searchLoading={searchLoading}
              onReloadCustomers={() => void customersQuery.refetch()}
              compact
            />

            <div className="table-meta-band table-meta-band--compact">
              <div className="selection-stats-inline">
                <StatChipInline n={filteredByStripe.length} label="on page" />
                <StatChipInline n={withLinkCount} label="with link" />
                <StatChipInline
                  n={
                    selectedWithLink.length > 0
                      ? selectedWithLink.length
                      : allMatchingWithLink.length
                  }
                  label="in CSV"
                />
              </div>

              <InvoiceTableToolbar
                pageSize={filters.pageSize}
                search={filters.tableSearch}
                findPlaceholder="Invoice number or URL…"
                findTitle="Client-side — current page only. Matches invoice number or hosted URL fragment."
                onPageSizeChange={(size) => {
                  filters.setPageSize(size);
                  clearSelection();
                }}
                onSearchChange={filters.setTableSearch}
                selection={{
                  pageSelectableCount,
                  allOnPageSelected: selection.allOnPageSelected,
                  someOnPageSelected: selection.someOnPageSelected,
                  onTogglePage: handleToggleAllOnPage,
                  totalSelectedCount: selectedWithLink.length,
                  onClearSelection: clearSelection,
                }}
              />
            </div>
          </>
        }
        table={
          <div className="panel invoices-workspace">
        {isLoading && rawItems.length === 0 ? (
          <p className="px-4 py-6 text-sm text-[var(--muted)]">Refreshing…</p>
        ) : null}
        {!error && rawItems.length > 0 ? (
          <PayLinksTable
            items={items}
            customers={customers}
            selected={selection.selected}
            onToggle={handleToggleRow}
            embedded
            fixedColumnLayout
          />
        ) : null}
        {!isLoading && !isFetching && !error && rawItems.length === 0 ? (
          <p className="empty-state px-4 py-8 text-sm text-[var(--muted)]">
            No invoices match these filters.
          </p>
        ) : null}

        {csvToast ? (
          <p className="export-toast mx-4 mb-2 text-sm text-emerald-700">{csvToast}</p>
        ) : null}
          </div>
        }
        pager={
          <InvoiceCursorPager
            compact
            itemCount={rawItems.length}
            page={filters.page}
            canGoPrev={filters.canGoPrev}
            hasMore={data?.has_more ?? false}
            pageLoading={isFetching && isPlaceholderData}
            onPrev={filters.goPrev}
            onNext={() => {
              const reason = withGate("paginate", () => {
                if (data?.next_cursor) filters.goNext(data.next_cursor);
              });
              if (reason) openGate(reason, "paginate");
            }}
            detail={
              filters.tableSearch && items.length !== rawItems.length
                ? `(${items.length} after find in table)`
                : filters.tableSearch
                  ? "(find in table active)"
                  : undefined
            }
          />
        }
        footer={
          <div
            className={cn(
              "export-sticky export-sticky--compact",
              filters.isDirty && "is-pending-search",
            )}
          >
            {csvExportActions}
          </div>
        }
      />

      <BillingGateModal
        open={billingGate != null}
        reason={billingGate?.reason ?? null}
        capability={billingGate?.capability}
        onClose={() => setBillingGate(null)}
      />
    </>
  );
}
